import { mrrDasEmpresas } from './mrrDoGrupo';
/**
 * GRUPO ECONÔMICO: a matriz e as empresas ligadas a ela, e quanto o grupo fatura.
 *
 * ⛔ O DEFEITO QUE ABRIU ISTO (01/10/2026). A tela "Filiais" mostrava R$ 79,98
 * de MRR para o grupo PARANAFRIGOR, que fatura R$ 685,30: o card somava só os
 * contratos do cliente-matriz. Existem DOIS jeitos de uma empresa pertencer ao
 * grupo, e os dois estão em produção:
 *
 *  (a) item do contrato da MATRIZ com `locationId` da filial. É o FESTVAL: as
 *      lojas entram na fatura da matriz e o `mrr` do contrato já as inclui.
 *  (b) cliente SEPARADO com contrato próprio, ligado por `parentClientId` ou
 *      pelo CNPJ igual ao de uma filial cadastrada. É a PARANAFRIGOR (TOTALFRIGOR,
 *      REDEFRIGOR, PRODUTIVA) e o CONCEITO BRASIL (PRÉ-FABRICADOS).
 *
 * Total do grupo = contratos ativos da matriz (que já contêm o caso a) + contratos
 * ativos dos clientes do caso b, cada um contado UMA vez.
 *
 * ⚠️ "Filial" estava errado em metade dos casos. A distinção sai da RAIZ do CNPJ
 * (8 primeiros dígitos), sem campo novo: raiz igual à da matriz é filial; raiz
 * diferente é coligada, outra empresa do mesmo grupo.
 */

export const soDigitos = (v: unknown): string => String(v ?? '').replace(/\D/g, '');
export const raizCnpj = (v: unknown): string => {
  const d = soDigitos(v);
  return d.length >= 8 ? d.slice(0, 8) : '';
};

const num = (v: unknown): number => {
  const n = parseFloat(String(v));
  return isFinite(n) ? n : 0;
};

export interface GClient {
  id: number;
  name?: string | null;
  tradeName?: string | null;
  companyName?: string | null;
  cnpj?: string | null;
  parentClientId?: number | null;
  /** 'Ativo' | 'Inativo' em produção. */
  status?: string | null;
}
export interface GLocation {
  id: number;
  clientId: number;
  code?: string | null;
  name?: string | null;
  companyName?: string | null;
  cnpj?: string | null;
  isActive?: boolean;
}
export interface GContract {
  id: number;
  clientId: number;
  status: string;
  mrr: number | string;
  items?: Array<{ type?: string; totalValue?: number | string; locationId?: number | null }>;
}

export type TipoDeVinculo = 'filial' | 'coligada' | null;

export interface Membro {
  /** De onde veio: filial cadastrada (client_locations) ou cliente ligado por parentClientId sem filial cadastrada. */
  origem: 'local' | 'cliente';
  location: GLocation | null;
  /** Cliente separado desta empresa (caso b), se existir. */
  cliente: GClient | null;
  vinculo: TipoDeVinculo;
  /** MRR que entra pela fatura da matriz (itens com locationId). */
  mrrNaMatriz: number;
  qtdItensNaMatriz: number;
  /** MRR dos contratos ativos do próprio cliente. */
  mrrProprio: number;
  temContratoProprio: boolean;
  /** Tem CNPJ e nada o resolve: nem item com o locationId, nem cliente com o CNPJ. */
  vinculoQuebrado: boolean;
}

export interface Grupo {
  matriz: GClient;
  /** Itens MRR da matriz sem locationId (a matriz em si). */
  mrrDaMatriz: number;
  /** Soma dos contratos ativos da matriz: já inclui o que as filiais consomem pela fatura dela. */
  mrrContratosMatriz: number;
  /** Empresas ativas do grupo: as que a tela lista e conta. */
  membros: Membro[];
  /** Empresas cujo cliente não está Ativo. Fora da listagem e da contagem, reveladas sob demanda. */
  membrosInativos: Membro[];
  mrrTotal: number;
  problemas: number;
}

export function tipoDeVinculo(cnpjMatriz: unknown, cnpjEmpresa: unknown): TipoDeVinculo {
  const a = raizCnpj(cnpjMatriz);
  const b = raizCnpj(cnpjEmpresa);
  if (!a || !b) return null;
  return a === b ? 'filial' : 'coligada';
}

const ativos = (contracts: GContract[], clientId: number) =>
  contracts.filter((c) => c.clientId === clientId && c.status === 'Ativo');

const somaMrr = (cs: GContract[]) => cs.reduce((s, c) => s + num(c.mrr), 0);

export function montarGrupos(clients: GClient[], locations: GLocation[], contracts: GContract[]): Grupo[] {
  const porCnpj = new Map<string, GClient>();
  for (const c of clients) {
    const d = soDigitos(c.cnpj);
    if (d) porCnpj.set(d, c);
  }
  const locsPorCliente = new Map<number, GLocation[]>();
  for (const l of locations) {
    const arr = locsPorCliente.get(l.clientId) || [];
    arr.push(l);
    locsPorCliente.set(l.clientId, arr);
  }
  const filhosPorPai = new Map<number, GClient[]>();
  for (const c of clients) {
    if (c.parentClientId != null) {
      const arr = filhosPorPai.get(c.parentClientId) || [];
      arr.push(c);
      filhosPorPai.set(c.parentClientId, arr);
    }
  }

  const grupos: Grupo[] = [];
  for (const matriz of clients) {
    const locs = locsPorCliente.get(matriz.id) || [];
    const filhos = filhosPorPai.get(matriz.id) || [];
    if (locs.length === 0 && filhos.length === 0) continue;

    const contratosMatriz = ativos(contracts, matriz.id);
    const itensMatriz = contratosMatriz.flatMap((c) => c.items || []);
    const mrrContratosMatriz = somaMrr(contratosMatriz);
    const mrrDaMatriz = itensMatriz
      .filter((i) => i.type === 'MRR' && !i.locationId)
      .reduce((s, i) => s + num(i.totalValue), 0);

    const membros: Membro[] = [];
    const clientesContados = new Set<number>();

    const membroDoCliente = (cli: GClient | null) => {
      if (!cli) return { mrrProprio: 0, temContratoProprio: false };
      const cs = ativos(contracts, cli.id);
      return { mrrProprio: somaMrr(cs), temContratoProprio: cs.length > 0 };
    };

    for (const l of locs) {
      const itens = itensMatriz.filter((i) => i.locationId === l.id);
      const itensMrr = itens.filter((i) => i.type === 'MRR');
      const d = soDigitos(l.cnpj);
      const achado = d ? porCnpj.get(d) : undefined;
      const cliente = achado && achado.id !== matriz.id ? achado : null;
      if (cliente) clientesContados.add(cliente.id);
      const proprio = membroDoCliente(cliente);
      membros.push({
        origem: 'local',
        location: l,
        cliente,
        vinculo: tipoDeVinculo(matriz.cnpj, l.cnpj),
        mrrNaMatriz: itensMrr.reduce((s, i) => s + num(i.totalValue), 0),
        qtdItensNaMatriz: itensMrr.length,
        ...proprio,
        vinculoQuebrado: !!d && itens.length === 0 && !cliente,
      });
    }

    for (const f of filhos) {
      if (clientesContados.has(f.id) || f.id === matriz.id) continue;
      clientesContados.add(f.id);
      membros.push({
        origem: 'cliente',
        location: null,
        cliente: f,
        vinculo: tipoDeVinculo(matriz.cnpj, f.cnpj),
        mrrNaMatriz: 0,
        qtdItensNaMatriz: 0,
        ...membroDoCliente(f),
        vinculoQuebrado: false,
      });
    }


    // EMPRESA INATIVA sai da listagem e da contagem, mas o vínculo continua no
    // banco: é histórico e verdadeiro. A TECH-FIN (cliente 240) encerrou em
    // agosto/2026 e a saída dela é o que motivou a redistribuição do rateio da
    // PARANAFRIGOR. O dinheiro não muda: os contratos dela estão cancelados, e
    // o total já só soma contratos ativos.
    const ativosNoGrupo = membros.filter((m) => !membroInativo(m));
    grupos.push({
      matriz,
      mrrDaMatriz,
      mrrContratosMatriz,
      membros: ativosNoGrupo,
      membrosInativos: membros.filter(membroInativo),
      // A MESMA conta do painel e da ficha (utils/mrrDoGrupo): cada empresa uma vez.
      mrrTotal: mrrDasEmpresas(contracts, [matriz.id, ...Array.from(clientesContados)]),
      problemas: ativosNoGrupo.filter((m) => m.vinculoQuebrado).length,
    });
  }
  return grupos;
}

/** O cliente desta empresa não está Ativo. Só o status do cliente decide. */
export function membroInativo(m: Membro): boolean {
  return !!m.cliente?.status && m.cliente.status !== 'Ativo';
}

/**
 * IDs dos clientes que são membros de algum grupo (não aparecem como "outros
 * clientes"). Inclui os inativos: continuam pertencendo ao grupo.
 */
export function idsDeMembros(grupos: Grupo[]): Set<number> {
  const s = new Set<number>();
  for (const g of grupos) {
    for (const m of [...g.membros, ...g.membrosInativos]) if (m.cliente) s.add(m.cliente.id);
  }
  return s;
}

/**
 * Busca por nome ou CNPJ. Testa `name` também: 55 clientes têm `tradeName`
 * vazio e só se acham pelo `name`. O CNPJ é comparado só em dígitos dos dois
 * lados: 3 clientes têm o CNPJ gravado sem pontuação e eram inalcançáveis por
 * busca pontuada.
 */
export function casaBusca(
  termo: string,
  alvo: { name?: string | null; tradeName?: string | null; companyName?: string | null; cnpj?: string | null; code?: string | null },
): boolean {
  const q = termo.trim().toLowerCase();
  if (!q) return true;
  const textos = [alvo.name, alvo.tradeName, alvo.companyName, alvo.code];
  if (textos.some((t) => String(t || '').toLowerCase().includes(q))) return true;
  const qd = soDigitos(q);
  return qd.length > 0 && soDigitos(alvo.cnpj).includes(qd);
}

export function grupoCasaBusca(termo: string, g: Grupo): boolean {
  if (casaBusca(termo, g.matriz)) return true;
  return g.membros.some((m) => (m.location && casaBusca(termo, m.location)) || (m.cliente && casaBusca(termo, m.cliente)));
}

/** Como a empresa é cobrada, em texto para a linha. */
export function textoDeCobranca(m: Membro): string {
  if (m.temContratoProprio && m.qtdItensNaMatriz > 0) return 'fatura própria + na fatura da matriz';
  if (m.temContratoProprio) return 'fatura própria';
  if (m.qtdItensNaMatriz > 0) return 'na fatura da matriz';
  return 'sem cobrança';
}

/**
 * AS LINHAS DE LOCAL DE UMA ASSINATURA, na tela de Assinaturas.
 *
 * ⛔ Até 02/10/2026 aquela tela resolvia cada local SÓ pelo `locationId` dos
 * itens da assinatura aberta. Acertava a FESTVAL (caso a) e mostrava "0 itens
 * · —" para as coligadas da PARANAFRIGOR (caso b), cujos contratos estão em
 * OUTROS clientes. E o lápis da coligada abria a assinatura da matriz, por isso
 * o editor dizia "Editar Itens · Matriz" ao se clicar na TOTALFRIGOR. Duas
 * telas respondiam diferente à mesma pergunta; agora as duas leem `montarGrupos`.
 *
 * Por linha:
 *  - `matriz`: a assinatura aberta tem itens com o locationId do local;
 *  - `propria`: não tem, e a empresa tem contrato ativo próprio. Os itens e o
 *    lápis são DESSE contrato;
 *  - `nenhuma`: nem um nem outro (o lápis segue abrindo a assinatura aberta,
 *    para lançar o primeiro item do local).
 *
 * As faturas próprias só entram na assinatura PRINCIPAL da matriz (o menor id
 * ativo): com duas assinaturas, mostrá-las nas duas somaria o grupo duas vezes.
 */
export type FonteDoLocal = 'matriz' | 'propria' | 'nenhuma';

type ItemDeContrato = NonNullable<GContract['items']>[number];

export interface LinhaDoLocal {
  chave: string;
  location: GLocation | null;
  cliente: GClient | null;
  codigo: string;
  nome: string;
  cnpj: string;
  vinculo: TipoDeVinculo;
  fonte: FonteDoLocal;
  /** De qual contrato vêm os itens e para qual o lápis aponta. */
  contrato: GContract;
  /** locationId a passar ao editor: o do local na matriz, ou null no contrato próprio. */
  locationIdNoEditor: number | null;
  itens: ItemDeContrato[];
}

export interface LocaisDaAssinatura {
  linhas: LinhaDoLocal[];
  /** Soma do MRR das linhas `propria`. */
  mrrFaturasProprias: number;
  /** MRR da assinatura + faturas próprias; null quando não há fatura própria. */
}

/**
 * OS LOCAIS QUE CONTAM numa assinatura (selo "+N locais" e tabela de locais).
 *
 * Legado do `syncLocationToClient`: a mesma empresa existe como LOCAL da matriz e
 * como CLIENTE filho do grupo (CONCEITO: local 43 = cliente 327, com contrato
 * próprio). Esse local é duplicata: mostrá-lo repete o que a linha de grupo já diz.
 *
 * ⛔ Duplicata é o local cujo CNPJ é de um cliente filho da mesma matriz E que NÃO
 * tem item alocado nesta assinatura. Local com item alocado é cobrança por local,
 * não duplicata: na FESTVAL, 72 dos 78 itens da assinatura #3 estão nos 36 locais
 * (medido em 04/10/2026), e escondê-los tiraria o detalhe por loja.
 * Local sem CNPJ, ou com CNPJ que não é de cliente do grupo, sempre conta.
 */
export function locaisQueContam(contrato: GContract, locations: GLocation[], clients: GClient[]): GLocation[] {
  const cnpjsDosFilhos = new Set(
    clients.filter((c) => c.parentClientId === contrato.clientId).map((c) => soDigitos(c.cnpj)).filter(Boolean),
  );
  const itens = contrato.items || [];
  return locations
    .filter((l) => l.clientId === contrato.clientId)
    .filter((l) => {
      const duplicata = !!soDigitos(l.cnpj) && cnpjsDosFilhos.has(soDigitos(l.cnpj));
      return !duplicata || itens.some((i) => i.locationId === l.id);
    });
}

export function locaisDaAssinatura(
  contrato: GContract,
  locations: GLocation[],
  contracts: GContract[],
  grupos: Grupo[],
): LocaisDaAssinatura {
  const grupo = grupos.find((g) => g.matriz.id === contrato.clientId);
  const principal = ativos(contracts, contrato.clientId).sort((a, b) => a.id - b.id)[0];
  const ehPrincipal = !!principal && principal.id === contrato.id;
  const itensDoContrato = contrato.items || [];

  const membroPorLocal = new Map<number, Membro>();
  const inativosPorLocal = new Set<number>();
  if (grupo) {
    for (const m of grupo.membros) if (m.location) membroPorLocal.set(m.location.id, m);
    for (const m of grupo.membrosInativos) if (m.location) inativosPorLocal.add(m.location.id);
  }

  const contratoProprio = (cli: GClient | null) =>
    cli ? ativos(contracts, cli.id).sort((a, b) => a.id - b.id)[0] || null : null;

  const linhas: LinhaDoLocal[] = [];
  for (const loc of locations.filter((l) => l.clientId === contrato.clientId)) {
    if (inativosPorLocal.has(loc.id)) continue;
    const membro = membroPorLocal.get(loc.id) || null;
    const itensNaMatriz = itensDoContrato.filter((i) => i.locationId === loc.id);
    const proprio = ehPrincipal && itensNaMatriz.length === 0 ? contratoProprio(membro?.cliente || null) : null;
    const base = {
      chave: `l${loc.id}`,
      location: loc,
      cliente: membro?.cliente || null,
      codigo: loc.code || `L${String(loc.id).padStart(2, '0')}`,
      nome: loc.name || '',
      cnpj: loc.cnpj || '',
      vinculo: grupo ? tipoDeVinculo(grupo.matriz.cnpj, loc.cnpj) : null,
    };
    if (proprio) {
      linhas.push({ ...base, fonte: 'propria', contrato: proprio, locationIdNoEditor: null, itens: proprio.items || [] });
    } else {
      linhas.push({
        ...base,
        fonte: itensNaMatriz.length > 0 ? 'matriz' : 'nenhuma',
        contrato,
        locationIdNoEditor: loc.id,
        itens: itensNaMatriz,
      });
    }
  }

  // Empresa ligada só por parentClientId (sem filial cadastrada), ativa e com contrato.
  if (grupo && ehPrincipal) {
    for (const m of grupo.membros) {
      if (m.origem !== 'cliente' || !m.cliente) continue;
      const proprio = contratoProprio(m.cliente);
      if (!proprio) continue;
      linhas.push({
        chave: `c${m.cliente.id}`,
        location: null,
        cliente: m.cliente,
        codigo: '—',
        nome: m.cliente.name || m.cliente.tradeName || m.cliente.companyName || `Cliente #${m.cliente.id}`,
        cnpj: m.cliente.cnpj || '',
        vinculo: m.vinculo,
        fonte: 'propria',
        contrato: proprio,
        locationIdNoEditor: null,
        itens: proprio.items || [],
      });
    }
  }

  const mrrFaturasProprias = linhas
    .filter((l) => l.fonte === 'propria')
    .reduce((s, l) => s + num(l.contrato.mrr), 0);
  return {
    linhas,
    mrrFaturasProprias,
  };
}
